import { Alert, AlertIcon, Spinner } from '@chakra-ui/react';
import React, { useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import FormStack from '../../../components/form-stack';
import InlineErrorState from '../../../components/states/InlineErrorState';
import InlineScopePrompt from '../../_shared/InlineScopePrompt';
import { translate } from '../../../i18n';
import { CoachingSessionDto } from '../../../models/coach/dto/coaching-session.dto';
import { useGetCourts } from '../../court-management/hooks/useGetCourts';
import { isUnreadable } from '../../analytics/utils/statValue';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useUpdateCoachingSessionCourt } from '../hooks/useUpdateCoachingSessionCourt';

interface EditCoachingSessionCourtModalProps {
    isOpen: boolean;
    onClose: () => void;
    session: CoachingSessionDto;
}

export const EditCoachingSessionCourtModal: React.FC<EditCoachingSessionCourtModalProps> = ({
    isOpen,
    onClose,
    session
}) => {
    const [selectedCourtId, setSelectedCourtId] = useState<number>(session.court?.id || 0);
    const { selectedLocation } = useSelectedLocation();
    const { data: courts, isError: isCourtsError, state: courtsReadState } = useGetCourts(selectedLocation?.id);
    const updateCoachingSessionCourtMutation = useUpdateCoachingSessionCourt(session.id);

    const handleSave = async () => {
        if (selectedCourtId === session.court?.id) {
            onClose();
            return;
        }

        try {
            await updateCoachingSessionCourtMutation.mutateAsync({ courtId: selectedCourtId });
            onClose();
        } catch (error) {
            // Error will be handled by the mutation's error state
        }
    };

    const handleClose = () => {
        setSelectedCourtId(session.court?.id || 0); // Reset to original court
        onClose();
    };

    const courtOptions = (courts || []).map((court) => ({
        value: String(court.id),
        label: court.id === session.court?.id
            ? `${court.name} (${translate('game.current')})`
            : court.name,
    }));

    const currentCourtOptions = session.court
        ? [{ value: String(session.court.id), label: `${session.court.name} (${translate('game.current')})` }]
        : [];

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            title={translate('coaching_session.edit_court')}
            onSubmit={handleSave}
            submitLabel={translate('save')}
            cancelLabel={translate('cancel')}
            isSubmitting={updateCoachingSessionCourtMutation.isLoading}
            isSubmitDisabled={!selectedCourtId || selectedCourtId === session.court?.id}
        >
            <FormStack>
                {updateCoachingSessionCourtMutation.error && (
                    <Alert status="error">
                        <AlertIcon />
                        {updateCoachingSessionCourtMutation.error instanceof Error
                            ? updateCoachingSessionCourtMutation.error.message
                            : translate('coaching_session.error_updating_court')}
                    </Alert>
                )}

                {session.court && (
                    <FormField
                        type="select"
                        label={translate('coaching_session.current_court')}
                        value={String(session.court.id)}
                        onChange={() => {}}
                        options={currentCourtOptions}
                        isDisabled
                    />
                )}

                {/*
                    `courtsReadState` rather than the raw `isLoading` (D62): run
                    134 gated `useGetCourts` on `locationId`, and React Query v4
                    reports a disabled query as `isLoading: true` with
                    `fetchStatus: 'idle'` — so the old flag would have left this
                    spinner turning forever whenever no club was selected, which
                    D64 rates strictly worse than the empty destination list it
                    replaced. `never-issued` gets the prompt instead: there is
                    nowhere to move this coaching session to until a club is chosen.
                */}
                {courtsReadState === 'loading' ? (
                    <Spinner size="sm" />
                ) : courtsReadState === 'never-issued' ? (
                    <InlineScopePrompt gate="location" />
                ) : isUnreadable(isCourtsError, courts) ? (
                    // An empty destination list here reads as "this venue has
                    // no other court to move the session to" (R89-4 / D57).
                    <InlineErrorState message={translate('courts.error_loading')} />
                ) : (
                    <FormField
                        type="select"
                        label={translate('coaching_session.new_court')}
                        value={String(selectedCourtId)}
                        onChange={(value) => setSelectedCourtId(Number(value))}
                        options={courtOptions}
                        placeholder={translate('courts.select_court')}
                    />
                )}
            </FormStack>
        </FormModal>
    );
};
